import _ from "lodash";
import { useEffect, useState } from "react";
import {
  PageContainer,
  PageContent,
  PageFooterBar,
} from "../../components/PageContainer";
import apiAgent from "../../api/apiAgent";
import { useNavigate, useParams } from "react-router";
import { BreadCrumbs, TitleBar } from "../../components/TitleBar";
import { useAtomValue } from "jotai";
import { modelDataAtom, useValidateModel } from "../../atoms";
import {
  Button,
  BackButton,
  DeleteButton,
  EditButton,
} from "../../components/Button";
import { Link } from "react-router-dom";
import { getDataAndSchemaFromModel } from "../../components/Forms/utils";
import { DisplayValue } from "../../components/DisplayValue";
import { RelatedItemLists } from "../../components/Lists/ItemLists";
import { tMsg } from "../../components/ToastNotifications";
import { checkTitle, useOriginParams } from "../../utils";
import { useSetChangesMade } from "../../components/Publishing/utils";
import { DeletePopup } from "../../components/ConfirmationPopup";

const BaseShowItem = (): JSX.Element => {
  const params = useParams();
  const [originFrom, originId, originParams] = useOriginParams();
  const modelName = params.model?.toString() || "";
  useValidateModel(modelName);
  const setChanges = useSetChangesMade(modelName);

  const modelData = useAtomValue(modelDataAtom);
  const [schema, setSchema] = useState<any[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState<boolean>(true);
  const [itemData, setItemData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);

  const navigate = useNavigate();
  const displayTitle = checkTitle(modelName);

  useEffect(() => {
    fetchData();
    // if (modelData && modelData !== null && isLoadingSchema) {
    const [, getSchema, getRelationships] = getDataAndSchemaFromModel(
      modelData,
      modelName,
      false
    );
    // we don't set item data for update
    setSchema(getSchema);
    setRelationships(getRelationships);
    setIsLoadingSchema(false);
    // }
  }, [modelData, modelName]);

  const fetchData = async () => {
    try {
      const response = await apiAgent.DataType.getAll(modelName);

      const { data } = response;
      const item =
        data.find((item: any) => _.toString(item.id) === params.id) || null;

      setItemData(item);
    } catch (err: any) {
      console.log(err);
      if (err.response?.status === 401) {
        navigate("/logout");
      } else tMsg.error("Error retrieving data"); // TODO: put this back after debugging
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePressed = async () => {
    try {
      if (!params.id) throw "No ID specified.";
      const response = await apiAgent.DataType.delete({
        model_name: modelName,
        target_ids: [_.toNumber(params.id)],
      });
      if (response.status === 200) {
        tMsg.success(`${displayTitle} deleted`);
        setChanges();
        if (originFrom && originId)
          navigate(`/m/${originFrom}/show/${originId}`);
        else navigate(`/m/${modelName}`);
      }
    } catch (err) {
      // TODO: add any error in response to notifications here
      tMsg.error(`Error deleting ${modelName}`);
      console.log(err);
    }
  };

  const crumbs =
    originId && originFrom
      ? [
          {
            label: checkTitle(originFrom),
            link: `/m/${originFrom}`,
          },
          {
            label: `${originId}`,
            link: `/m/${originFrom}/show/${originId}`,
          },
        ]
      : [
          {
            label: displayTitle,
            link: `/m/${modelName}`,
          },
        ];

  const useSchema = schema.filter(
    (item) => item.name !== "id" && item.name !== "sort_id"
  );

  return (
    <PageContainer
      isLoading={isLoading || isLoadingSchema}
      className="relative"
    >
      <PageContent reduceHeightForFooter>
        <BreadCrumbs crumbs={crumbs} />
        {
          // TODO: Create a better display if id/itemData is invalid
          params.id && itemData !== null ? (
            <>
              <TitleBar title={displayTitle}>
                <BackButton />
              </TitleBar>
              <div className="flex flex-col gap-4">
                {useSchema.map((item, index) => (
                  <DisplayValue
                    key={index}
                    name={item.name}
                    model={modelName}
                    value={itemData[item.name]}
                    type={item.type}
                  />
                ))}
              </div>
              {
                // Show any items related to this one and
                // give the option to create a new related item
                relationships && relationships.length > 0 && (
                  <RelatedItemLists
                    relationships={relationships}
                    model={modelName}
                    id={parseInt(params.id)}
                  />
                )
              }
              {itemData.thumbUrl && (
                <img src={itemData.thumbUrl} className="max-w-full py-4" />
              )}
              {itemData.fullImageUrl && (
                <img src={itemData.fullImageUrl} className="max-w-full py-4" />
              )}
            </>
          ) : (
            <TitleBar title={`Item ${params.id} not found`} />
          )
        }
      </PageContent>
      <PageFooterBar>
        <Link
          to={`/m/${modelName}/edit/${params.id}${
            originParams ? originParams : ""
          }`}
        >
          <EditButton />
        </Link>
        <DeleteButton onClick={() => setShowDeletePopup(true)} />
      </PageFooterBar>
      <DeletePopup
        confirmAction={() => {
          handleDeletePressed();
          setShowDeletePopup(false);
        }}
        setShowModal={setShowDeletePopup}
        showModal={showDeletePopup}
      />
    </PageContainer>
  );
};

export default BaseShowItem;
